import type { Event } from '@sentry/core';
import { documentUrlOf, isDocumentFrame } from './frameUrl.js';

/**
 * WebView-injected scripts (`evaluateJavascript`, an injected `<script>` node) are attributed to the
 * document URL at line 1 and reach `onerror` as one anonymous frame, so the event carries nothing
 * actionable. Shape-based rather than message-based, so it covers every injected snippet: our own
 * static inline scripts keep their document-relative line and stay reportable.
 */
export function isInjectedScriptNoise(event: Event): boolean {
  const values = event.exception?.values;
  const exception = values?.length === 1 ? values[0] : undefined;
  if (!exception) {
    return false;
  }

  const { mechanism, stacktrace } = exception;
  if (mechanism?.type !== 'onerror' || mechanism.handled !== false) {
    return false;
  }

  const frames = stacktrace?.frames;
  const frame = frames?.length === 1 ? frames[0] : undefined;
  if (!frame?.filename) {
    return false;
  }

  // Sentry's browser stack parsers normalize an unnamed frame to '?'.
  if (frame.function && frame.function !== '?') {
    return false;
  }
  if ((frame.lineno ?? 0) > 1) {
    return false;
  }

  const documentUrl = documentUrlOf(event);
  return documentUrl ? isDocumentFrame(frame.filename, documentUrl) : false;
}
